'use client';
import React, { useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  MapIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SignalIcon,
  Cog6ToothIcon,
  UserIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  CameraIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { getEffectiveDotColor } from '@/data/dotsData';
import { useGrid, GRID_COLS, GRID_TOTAL } from '@/context/GridContext';
import { useAlerts } from '@/context/AlertContext';
import { useState, useMemo } from 'react';

const NAV_ITEMS = [
  { href: '/', icon: MapIcon, label: 'Mapa Interactivo', badge: null },
  { href: '/camera-assignments-report', icon: DocumentTextIcon, label: 'Reporte de Cámaras', badge: null },
  { href: '/camera-timeline', icon: ClockIcon, label: 'Línea de Tiempo', badge: null },
  { href: '/audit-log', icon: ClipboardDocumentListIcon, label: 'Bitácora de Auditoría', badge: null },
  { href: '/alertas', icon: BellAlertIcon, label: 'Alertas', badge: 'alerts' },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [showCameraGrid, setShowCameraGrid] = useState(true);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; idx: number } | null>(null);
  const pathname = usePathname();

  const {
    gridCells,
    removeDotFromGrid,
    clearAllCells,
    selectedGridIndices,
    toggleGridSelection,
    clearGridSelection,
  } = useGrid();

  const { sentAlerts } = useAlerts();
  const pendingAlerts = sentAlerts.filter(a => a.recipients.some(r => !r.acknowledged)).length;

  // Sort priority: alarma(0) > streaming(1) > blue/activo(2) > yellow/desconectado(3) > red/offline(4) > empty(5)
  const STATUS_PRIORITY: Record<string, number> = {
    alarma: 0,
    streaming: 1,
    blue: 2,
    yellow: 3,
    red: 4,
  };

  // Build sorted cells: filled cells sorted by status, then empty cells at the end
  const sortedGridCells = useMemo(() => {
    const filled = gridCells
      .map((cell, originalIdx) => ({ cell, originalIdx }))
      .filter(({ cell }) => cell !== null);

    filled.sort((a, b) => {
      const colorA = a.cell ? getEffectiveDotColor(a.cell.dot) : 'red';
      const colorB = b.cell ? getEffectiveDotColor(b.cell.dot) : 'red';
      const prioA = STATUS_PRIORITY[colorA] ?? 4;
      const prioB = STATUS_PRIORITY[colorB] ?? 4;
      return prioA - prioB;
    });

    const empty = gridCells
      .map((cell, originalIdx) => ({ cell, originalIdx }))
      .filter(({ cell }) => cell === null);

    return [...filled, ...empty];
  }, [gridCells]);

  const handleCellClick = useCallback((e: React.MouseEvent, idx: number) => {
    if (!gridCells[idx]) return;
    const multi = e.ctrlKey || e.metaKey || e.shiftKey;
    toggleGridSelection(idx, multi);
  }, [gridCells, toggleGridSelection]);

  const handleCellRightClick = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    if (!gridCells[idx]) return;
    setContextMenu({ x: e.clientX, y: e.clientY, idx });
  }, [gridCells]);

  const handleRemoveFromGrid = useCallback(() => {
    if (contextMenu === null) return;
    // If multiple selected and the right-clicked cell is among them, remove all selected
    if (selectedGridIndices.size > 1 && selectedGridIndices.has(contextMenu.idx)) {
      selectedGridIndices.forEach(idx => removeDotFromGrid(idx));
    } else {
      removeDotFromGrid(contextMenu.idx);
    }
    setContextMenu(null);
  }, [contextMenu, selectedGridIndices, removeDotFromGrid]);

  const handleDismissContext = useCallback(() => {
    setContextMenu(null);
  }, []);

  const filledCount = gridCells.filter(Boolean).length;
  const sidebarWidth = collapsed ? 64 : showCameraGrid ? 280 : 220;

  return (
    <>
      <aside
        className="relative flex flex-col border-r border-border bg-card transition-all duration-300 ease-in-out shrink-0"
        style={{ width: sidebarWidth }}
        onClick={(e) => {
          handleDismissContext();
          // Clear grid selection if clicking outside cells
          const target = e.target as HTMLElement;
          if (!target.closest('[data-grid-cell]')) {
            clearGridSelection();
          }
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 px-3 py-4 border-b border-border min-h-[60px]">
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-semibold text-sm text-foreground tracking-tight truncate">
              PueblaMapCam
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-1 px-2 py-3 shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
              <SignalIcon className="w-3 h-3 text-blue-400 animate-pulse" />
              <span className="text-xs text-muted-foreground font-medium">EN VIVO</span>
            </div>
          )}
          {NAV_ITEMS?.map((item) => {
            const active = pathname === item?.href || (item?.href !== '/' && pathname?.startsWith(item?.href));
            const showBadge = item.badge === 'alerts' && pendingAlerts > 0;
            return (
              <Link
                key={`nav-${item?.href}`}
                href={item?.href}
                title={collapsed ? item?.label : undefined}
                className={`
                  flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-150 group relative
                  ${active
                    ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
              >
                <div className="relative shrink-0">
                  <item.icon className="w-4.5 h-4.5 shrink-0 w-[18px] h-[18px]" />
                  {showBadge && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-orange-500 rounded-full flex items-center justify-center text-[8px] font-bold text-white">
                      {pendingAlerts > 9 ? '9+' : pendingAlerts}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <span className="truncate flex-1">{item?.label}</span>
                )}
                {!collapsed && showBadge && (
                  <span className="ml-auto bg-orange-500/20 text-orange-400 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                    {pendingAlerts}
                  </span>
                )}
                {collapsed && (
                  <span className="absolute left-14 z-50 bg-card border border-border text-foreground text-xs px-2 py-1 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg">
                    {item?.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Camera Grid Section */}
        {!collapsed && (
          <div className="flex-1 flex flex-col min-h-0 border-t border-border">
            <button
              onClick={() => setShowCameraGrid(v => !v)}
              className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors shrink-0"
            >
              <div className="flex items-center gap-2">
                <CameraIcon className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold text-foreground">Vista Cámaras</span>
                <span className="text-[10px] bg-primary/10 text-primary rounded-full px-1.5 py-0.5 font-medium">
                  {filledCount}/{GRID_TOTAL}
                </span>
              </div>
              {showCameraGrid
                ? <ChevronUpIcon className="w-3 h-3 text-muted-foreground" />
                : <ChevronDownIcon className="w-3 h-3 text-muted-foreground" />
              }
            </button>

            {showCameraGrid && (
              <div className="flex-1 overflow-y-auto px-2 pb-2">
                <div className="flex items-center justify-between px-1 pb-1.5">
                  <p className="text-[9px] text-muted-foreground/60">
                    Clic para seleccionar · Ctrl+clic para multi · Clic derecho para quitar
                  </p>
                  {filledCount > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); clearAllCells(); }}
                      className="text-[9px] text-red-400 hover:text-red-300 font-medium transition-colors whitespace-nowrap ml-2 shrink-0"
                      title="Quitar todas las cámaras del grid"
                    >
                      Limpiar Grid
                    </button>
                  )}
                </div>
                {selectedGridIndices.size > 0 && (
                  <div className="flex items-center justify-between px-1 pb-1.5">
                    <span className="text-[9px] text-emerald-400 font-medium">
                      {selectedGridIndices.size} seleccionado{selectedGridIndices.size !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); clearGridSelection(); }}
                      className="text-[9px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Limpiar
                    </button>
                  </div>
                )}
                {/* 4×6 grid */}
                <div
                  className="grid gap-0.5"
                  style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}
                >
                  {sortedGridCells.map(({ cell, originalIdx }, displayIdx) => {
                    const isSelected = selectedGridIndices.has(originalIdx);
                    if (cell) {
                      const isA = cell.type === 'A';
                      const dotColor = getEffectiveDotColor(cell.dot);
                      const isLive = dotColor === 'blue';
                      const isDisconnected = dotColor === 'yellow';
                      const isAlarma = dotColor === 'alarma';
                      const isStreaming = dotColor === 'streaming';
                      // Status config
                      const statusBar = isAlarma
                        ? { bg: 'bg-red-500', label: 'ALARMA', pulse: true }
                        : isStreaming
                        ? { bg: 'bg-green-500', label: 'LIVE', pulse: true }
                        : isLive
                        ? { bg: 'bg-green-500', label: 'VIVO', pulse: true }
                        : isDisconnected
                        ? { bg: 'bg-yellow-400', label: 'DESC', pulse: false }
                        : { bg: 'bg-black', label: 'OFF', pulse: false };
                      return (
                        <div
                          key={`sg-${originalIdx}`}
                          data-grid-cell="true"
                          onClick={(e) => { e.stopPropagation(); handleCellClick(e, originalIdx); }}
                          onContextMenu={(e) => handleCellRightClick(e, originalIdx)}
                          title={`CAM-${cell.type}-${cell.dotId} · ${cell.dot.name} · ${isLive ? 'En vivo' : isDisconnected ? 'Desconectado' : 'Fuera de línea'}\nClic derecho para quitar`}
                          className={`
                            relative flex flex-col items-center justify-between rounded overflow-hidden aspect-square text-center
                            border cursor-pointer select-none transition-all duration-100
                            hover:scale-105 hover:shadow-md hover:z-10
                            ${isSelected
                              ? 'ring-2 ring-emerald-400 ring-offset-1 ring-offset-card scale-105 z-10' : ''
                            }
                            ${isA
                              ? 'bg-blue-500/15 border-blue-500/30 text-blue-400' : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                            }
                          `}
                        >
                          {/* Selection indicator */}
                          {isSelected && (
                            <span className="absolute top-0.5 left-0.5 w-2 h-2 rounded-full bg-emerald-400 flex items-center justify-center z-10">
                              <span className="text-[5px] text-black font-bold">✓</span>
                            </span>
                          )}
                          {/* Main content */}
                          <div className="flex flex-col items-center justify-center flex-1 pt-0.5">
                            {/* Cam type */}
                            <span className={`text-[7px] font-bold leading-none ${isA ? 'text-blue-500' : 'text-emerald-500'}`}>
                              {cell.type}
                            </span>
                            {/* ID */}
                            <span className="text-[9px] font-bold leading-none mt-0.5">
                              #{cell.dotId}
                            </span>
                          </div>
                          {/* Status bar — full width at bottom */}
                          <div className={`w-full flex items-center justify-center gap-0.5 py-[2px] ${statusBar.bg}`}>
                            {statusBar.pulse && (
                              <span className="w-1 h-1 rounded-full bg-white animate-pulse shrink-0" />
                            )}
                            <span className="text-[6px] font-bold text-white leading-none tracking-wide">
                              {statusBar.label}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={`sg-empty-${displayIdx}`}
                        data-grid-cell="true"
                        className="flex items-center justify-center rounded aspect-square border border-border/20 bg-muted/5"
                      >
                        <span className="text-[7px] text-muted-foreground/20">{displayIdx + 1}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-2 flex items-center gap-2 flex-wrap px-0.5">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-blue-500/20 border border-blue-500/40" />
                    <span className="text-[8px] text-muted-foreground">Cam A</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-emerald-500/20 border border-emerald-500/40" />
                    <span className="text-[8px] text-muted-foreground">Cam B</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-green-500" />
                    <span className="text-[8px] text-muted-foreground">Activo</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-yellow-400" />
                    <span className="text-[8px] text-muted-foreground">Desc.</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-red-500" />
                    <span className="text-[8px] text-muted-foreground">Offline</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-2 py-3 border-t border-border flex flex-col gap-1 shrink-0">
          <button
            className="flex items-center gap-3 px-2 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 text-sm w-full"
            title={collapsed ? 'Configuración' : undefined}
          >
            <Cog6ToothIcon className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Configuración</span>}
          </button>
          <button
            className="flex items-center gap-3 px-2 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 text-sm w-full"
            title={collapsed ? 'Operador' : undefined}
          >
            <UserIcon className="w-4 h-4 shrink-0" />
            {!collapsed && (
              <div className="flex flex-col items-start min-w-0">
                <span className="text-xs font-medium text-foreground truncate">Operador Principal</span>
                <span className="text-xs text-muted-foreground truncate">Turno Activo</span>
              </div>
            )}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-[72px] z-10 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 shadow-md"
          aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
        >
          {collapsed ? <ChevronRightIcon className="w-3 h-3" /> : <ChevronLeftIcon className="w-3 h-3" />}
        </button>
      </aside>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          className="fixed z-[9999] bg-card border border-border rounded-lg shadow-xl py-1 min-w-[160px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 border-b border-border">
            <span className="text-[10px] text-muted-foreground font-medium">
              {selectedGridIndices.size > 1 && selectedGridIndices.has(contextMenu.idx)
                ? `${selectedGridIndices.size} celdas seleccionadas`
                : `CAM-${gridCells[contextMenu.idx]?.type}-${gridCells[contextMenu.idx]?.dotId}`
              }
            </span>
          </div>
          <button
            onClick={handleRemoveFromGrid}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <span className="w-3 h-3 text-red-400">✕</span>
            {selectedGridIndices.size > 1 && selectedGridIndices.has(contextMenu.idx)
              ? `Quitar ${selectedGridIndices.size} del grid`
              : 'Quitar del grid'
            }
          </button>
          <button
            onClick={handleDismissContext}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
        </div>
      )}
    </>
  );
}